import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  List, 
  ListItem, 
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { autoLogoutManager } from '../utils/autoLogout';

const AutoLogoutTest: React.FC = () => {
  const [logs, setLogs] = useState<string[]>([]);
  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const [showWarning, setShowWarning] = useState(false);
  const [warningTimeLeft, setWarningTimeLeft] = useState(30);

  useEffect(() => {
    // Override console.log to capture logs
    const originalLog = console.log;
    console.log = (...args) => {
      const message = args.join(' ');
      if (message.includes('Admin:') || message.includes('Auto logout')) {
        setLogs(prev => [...prev.slice(-20), `${new Date().toLocaleTimeString()}: ${message}`]);
      }
      originalLog(...args);
    };

    // Initialize auto logout
    autoLogoutManager.initialize(
      () => {
        setIsLoggedIn(false);
        setShowWarning(false);
        setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: LOGGED OUT`]);
      },
      (timeLeft) => {
        setShowWarning(true);
        setWarningTimeLeft(timeLeft);
      }
    );

    return () => {
      console.log = originalLog;
      autoLogoutManager.cleanup();
    };
  }, []);

  const handleExtendSession = () => {
    setShowWarning(false);
    autoLogoutManager.extendSession();
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setShowWarning(false);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const simulateActivity = () => {
    // Trigger activity manually
    document.dispatchEvent(new MouseEvent('mousedown'));
  };

  if (!isLoggedIn) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Auto Logout Test - LOGGED OUT
        </Typography>
        <Button 
          variant="contained"
          onClick={() => setIsLoggedIn(true)}
        >
          Login Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Auto Logout Test - Admin Panel
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Status
            </Typography>
            <Typography>Logged In: {isLoggedIn ? 'Yes' : 'No'}</Typography>
            <Typography>Warning Shown: {showWarning ? 'Yes' : 'No'}</Typography>
            <Typography>Time Left: {warningTimeLeft}s</Typography>
            <Typography>Token Expired: {autoLogoutManager.isTokenExpired() ? 'Yes' : 'No'}</Typography>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button 
                variant="contained"
                color="success"
                onClick={simulateActivity}
              >
                Simulate Activity
              </Button>
              <Button 
                variant="contained"
                color="primary"
                onClick={handleExtendSession}
              >
                Extend Session
              </Button>
              <Button 
                variant="contained"
                color="error"
                onClick={handleLogout}
              >
                Logout
              </Button>
              <Button 
                variant="outlined"
                onClick={clearLogs}
              >
                Clear Logs
              </Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: '#1e1e1e', color: '#00ff00' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
              Debug Logs
            </Typography>
            <Box sx={{ height: 300, overflow: 'auto', fontFamily: 'monospace', fontSize: '0.875rem' }}>
              {logs.length === 0 ? (
                <Typography sx={{ color: '#888' }}>
                  No logs yet... Move your mouse or click to generate activity.
                </Typography>
              ) : (
                <List dense>
                  {logs.map((log, index) => (
                    <ListItem key={index} sx={{ py: 0 }}>
                      <ListItemText primary={log} />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" gutterBottom>
              Instructions
            </Typography>
            <List>
              <ListItem>
                <ListItemText primary="Stop moving your mouse for 4.5 minutes to see the warning" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Click 'Simulate Activity' to reset the timer" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Click 'Extend Session' when warning appears to test the Stay Logged In functionality" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Watch the debug logs to see what's happening" />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={showWarning} maxWidth="sm" fullWidth>
        <DialogTitle>Session Timeout Warning</DialogTitle>
        <DialogContent>
          <Typography>
            You will be logged out in {warningTimeLeft} seconds
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExtendSession} variant="contained" color="primary">
            Stay Logged In
          </Button>
          <Button onClick={handleLogout} variant="outlined">
            Logout Now
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutoLogoutTest;
